import { useContext, useMemo, type ReactNode } from "react";
import { PickupContext } from "./PickupContext";
import { PickupLocationContext } from "./PickupLocationContext";
import { SubscriptionsContext } from "./SubscriptionsContext";
import { DeliveryContext, type DeliveryContextType } from "./DeliveryContext";
import type { Delivery } from "../utils/types";

interface DeliveryProviderProps {
  children: ReactNode;
}

export const DeliveryContextProvider = ({
  children,
}: DeliveryProviderProps) => {
  const { pickups, loading, error } = useContext(PickupContext);
  const { pickupLocations } = useContext(PickupLocationContext);
  const { subscriptions, tiers } = useContext(SubscriptionsContext);

  const activeTierIds = useMemo(() => {
    const tierIds = new Set<string>();
    for (const subscription of subscriptions) {
      if (!(subscription.isActive ?? false)) continue;
      const subscriptionTiers =
        subscription.tiers.length > 0
          ? subscription.tiers
          : tiers.filter((tier) => tier.subscriptionId === subscription.id);
      for (const tier of subscriptionTiers) {
        tierIds.add(tier.id);
      }
    }
    return tierIds;
  }, [subscriptions, tiers]);

  // Transform Pickup[] to Delivery[] format; only include active subscriptions (ready for delivery).
  const deliveries: Delivery[] = useMemo(() => {
    return pickups
      .filter((pickup) => activeTierIds.has(pickup.subscriptionTierId))
      .map((pickup) => {
        const location = pickupLocations.find(
          (loc) => loc._id === pickup.pickupLocationId,
        );
        const pickupLocationName = location?.name || pickup.pickupLocationId;

        return {
          id: pickup.id,
          firstName: pickup.firstName,
          surname: pickup.surname,
          phoneNumber: pickup.phoneNumber,
          email: pickup.email,
          pickupLocation: pickupLocationName,
          day: pickup.pickupDay,
          subscriptionTierId: pickup.subscriptionTierId,
          subscriptionPaymentStatus: pickup.subscriptionPaymentStatus,
          friendPickup:
            pickup.friendPickupFirstName &&
            pickup.friendPickupLastName &&
            pickup.friendPickupPhone
              ? {
                  firstName: pickup.friendPickupFirstName,
                  surname: pickup.friendPickupLastName,
                  phoneNumber: pickup.friendPickupPhone,
                }
              : undefined,
        };
      });
  }, [pickups, pickupLocations, activeTierIds]);
  const value: DeliveryContextType = {
    deliveries,
    loading,
    error,
  };
  return (
    <DeliveryContext.Provider value={value}>
      {children}
    </DeliveryContext.Provider>
  );
};
